import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import McSessionDashboard from "../components/McSessionDashboard";
import { buildSessionDashboard, buildSessionHistory } from "../utils/mcSessionDashboard";
import { MorpheusCountBillingContent } from "./MorpheusCountBilling";
import { MorpheusCountTicketsContent } from "./MorpheusCountTickets";
import "./mcWorkspace.css";
import "../components/mcSessionDashboard.css";
import { loadArchivedSessionRecords, loadEngineState, loadSessionRecords, MC_SESSION_EVENT } from "../utils/mcEngineData";
import { buildResponseEventSummary, formatResponseDuration, getResponseEventAgeMinutes, getResponseSlaLimitMinutes, loadResponseEvents, MC_RESPONSE_EVENT, responseSeverityLabel, responseStatusLabel, updateResponseEventStatus, verifyResponseCorrection } from "../utils/mcResponseEvents";
import { buildNotificationSummary, loadEmailDeliveryConfig, loadNotificationRules, loadResponseNotifications, loadWhatsappDeliveryConfig, MC_NOTIFICATION_EVENT, notificationStatusLabel, processQueuedEmailNotifications, processQueuedWhatsappNotifications, processDeliveryQueue, retryNotification, saveEmailDeliveryConfig, saveNotificationRules, saveWhatsappDeliveryConfig, updateNotificationStatus } from "../utils/mcNotifications";
import { getMcActiveOrgId, mcGetItem, mcSubscribeCloudItem } from "../utils/mcCloudSync";
import { loadSites } from "../utils/mcOrgData";
import { createResponseRecipientRule, deleteResponseRecipientRule, loadResponseRecipientRules, parseRecipientEmails, parseRecipientPhones, RECIPIENT_CHANNEL_OPTIONS, RECIPIENT_ROLE_OPTIONS, saveResponseRecipientRules, updateResponseRecipientRule } from "../utils/mcResponseRecipients";
import { subscribeSessionListLive } from "../utils/mcLiveSessionSync";
import { projectDashboardSessionFromLedger } from "../utils/mcCountLedgerProjection";
import { formatStoreLabel, getAdminStoreView, getProfileAllowedSiteIds, isAdminProfile, canFinalizeCount, useAdminStoreView, useCurrentMcProfile } from "../utils/profileAccess";

const detailStorageKeyFor = (sessionId) => `mc-session-engine:${sessionId}`;

function roundTo2(value) {
  const n = Number(String(value ?? 0).replace(/,/g, ".") || 0);
  return Math.round(n * 100) / 100;
}

function countNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(String(value).replace(/,/g, "."));
  return Number.isFinite(n) ? n : 0;
}

function calculateArchivedExposureFromLines(lines) {
  if (!Array.isArray(lines) || !lines.length) return 0;
  let total = 0;
  lines.forEach((line) => {
    const direct = line?.costVariance ?? line?.finalCostVariance ?? line?.varianceValue;
    if (direct !== undefined && direct !== null && direct !== "") {
      total += Number(direct) || 0;
      return;
    }

    const hasCounted = line?.counted !== "" && line?.counted !== undefined && line?.counted !== null;
    const hasWaste = line?.waste !== "" && line?.waste !== undefined && line?.waste !== null;
    const touched = hasCounted || hasWaste || Boolean(String(line?.note || line?.notes || "").trim());
    if (!touched) return;

    const counted = countNumber(line?.counted);
    const waste = countNumber(line?.waste);
    const soh = Number(line?.systemSoh ?? line?.systemSOH ?? line?.soh ?? 0);
    const unitCost = Number(line?.cost || line?.retail_price || line?.price || 0);
    total += (counted + waste - soh) * unitCost;
  });
  return roundTo2(total || 0);
}

function loadArchivedDetailSnapshot(sessionId) {
  if (!sessionId) return null;
  try {
    const parsed = JSON.parse(mcGetItem(detailStorageKeyFor(sessionId)) || "null");
    return parsed && Array.isArray(parsed.lines) ? parsed : null;
  } catch (_) {
    return null;
  }
}

function resolveDashboardArchivedRecord(record = {}) {
  if (!record?.sessionId) return record;
  const detail = loadArchivedDetailSnapshot(record.sessionId) || (record?.detailSnapshot && Array.isArray(record.detailSnapshot.lines) ? record.detailSnapshot : null);
  const meta = detail?.meta || {};
  const lines = Array.isArray(detail?.lines) ? detail.lines : [];
  const detailExposure = calculateArchivedExposureFromLines(lines);
  const recordExposure = record.finalExposure ?? record.costExposure;
  const metaExposure = meta.finalExposure ?? meta.costExposure;
  const hasDetailLines = Array.isArray(lines) && lines.length > 0;
  const hasRecordExposure = recordExposure !== undefined && recordExposure !== null && recordExposure !== "" && Number.isFinite(Number(recordExposure));
  const hasMetaExposure = metaExposure !== undefined && metaExposure !== null && metaExposure !== "" && Number.isFinite(Number(metaExposure));

  // Dashboard must match what the archived session detail page shows.
  // Some older archive summary records still carry stale header exposure values
  // (for example a large old costExposure), while the opened archived session
  // correctly recalculates from the saved detail lines. When detail lines exist,
  // they are the safest source for dashboard cards, trend buckets and recent
  // session health. Only fall back to the archive-list/meta exposure if the
  // detail snapshot is not available.
  const exposure = hasDetailLines
    ? Number(detailExposure || 0)
    : hasRecordExposure
      ? Number(recordExposure || 0)
      : hasMetaExposure
        ? Number(metaExposure || 0)
        : 0;

  const archiveReason = String(record.archiveReason || meta.archiveReason || "").toLowerCase();
  const archiveType = String(record.archiveType || meta.archiveType || "").toLowerCase();
  const status = String(record.status || meta.status || "").toLowerCase();
  const detailTotal = lines.length;
  const totalLines = Number(record.finalTotalLines ?? record.totalLines ?? meta.totalLines ?? detailTotal ?? 0);
  const completedLinesRaw = Number(record.finalCompletedLines ?? record.completedLines ?? meta.completedLines ?? 0);
  const completedByReason = archiveType === "completed" || status === "completed" || archiveReason.includes("completed") || (totalLines > 0 && completedLinesRaw >= totalLines);
  const completedLines = completedByReason ? (detailTotal || totalLines) : completedLinesRaw;

  return {
    ...record,
    dashboardSource: "archived",
    archiveType: completedByReason ? "completed" : (record.archiveType || meta.archiveType || "incomplete"),
    status: completedByReason ? "completed" : (record.status || meta.status || "incomplete"),
    storeName: record.storeName || meta.storeName || meta.store || record.store || "",
    store: record.store || meta.store || meta.storeName || record.storeName || "",
    startedBy: record.startedBy || meta.startedBy || meta.createdBy || "User",
    lastEditedBy: record.lastEditedBy || meta.lastEditedBy || record.startedBy || meta.startedBy || "User",
    startedAt: record.startedAt || meta.startedAt || meta.createdAt || detail?.savedAt || record.archivedAt,
    updatedAt: record.updatedAt || detail?.savedAt || meta.updatedAt || record.archivedAt,
    totalLines: detailTotal || totalLines,
    completedLines,
    flaggedCount: Number(record.finalFlagCount ?? record.flaggedCount ?? meta.flaggedCount ?? 0),
    costExposure: roundTo2(exposure || 0),
    finalExposure: roundTo2(exposure || 0),
    dashboardVarianceKnown: hasDetailLines || hasRecordExposure || hasMetaExposure,
  };
}

function formatDateTime(value) {
  const date = new Date(value || 0);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" });
}

function formatCurrency(value) {
  const amount = Number(value || 0);
  const abs = Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: Math.abs(amount) % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `-R${abs}` : `R${abs}`;
}

function formatEventDateTime(value) {
  const time = new Date(value || 0);
  if (Number.isNaN(time.getTime())) return "—";
  return time.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function responseAgeText(event = {}) {
  const minutes = getResponseEventAgeMinutes(event);
  const status = String(event.status || "open").toLowerCase();
  if (status === "resolved") return `Resolved in ${formatResponseDuration(minutes)}`;
  if (status === "dismissed") return `Dismissed after ${formatResponseDuration(minutes)}`;
  return `Open for ${formatResponseDuration(minutes)}`;
}

function responseSlaText(event = {}) {
  const limit = getResponseSlaLimitMinutes(event);
  return formatResponseDuration(limit);
}

function responseSignalText(event = {}) {
  const severity = responseSeverityLabel(event.severity);
  const exposure = formatCurrency(event.exposure);
  const flags = Number(event.flaggedProducts || 0);
  return `${severity} · ${exposure} · ${flags} flagged product${flags === 1 ? "" : "s"}`;
}

const RESPONSE_ROOT_CAUSES = [
  "Counting error",
  "Receiving issue",
  "Transfer issue",
  "Theft suspected",
  "System issue",
  "Damaged stock",
  "Supplier issue",
  "Unknown",
];

function profileDisplayName(profile = {}) {
  return String(profile.name || profile.displayName || profile.fullName || profile.email || profile.role || "").trim();
}

function sessionDate(record) {
  const archivedLike = record?.archived === true || record?.archivedAt || record?.archiveType || record?.dashboardSource === "archived";
  const value = archivedLike
    ? (record.archivedAt || record.updatedAt || record.startedAt || record.createdAt || 0)
    : (record.updatedAt || record.startedAt || record.createdAt || record.archivedAt || 0);
  return new Date(value).getTime();
}

function storeCompareKey(value = "") {
  return String(formatStoreLabel(value) || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function sessionStoreCandidates(record = {}) {
  // Prefer the real session/site fields first. Mapping/config can be stale after
  // switching sites, so only use those as a fallback when the session has no
  // direct store identity. This stops dashboard bleed like Kuils River/Bothasig
  // appearing while District Six is selected.
  const direct = [
    record?.storeName,
    record?.store,
    record?.siteName,
    record?.storeKey,
    record?.siteId,
  ].filter(Boolean);
  if (direct.length) return direct;
  return [record?.mapping?.systemSoh, record?.config?.storeName].filter(Boolean);
}


function sessionStoreValue(record = {}) {
  const direct = record?.storeName || record?.store || record?.siteName || record?.storeKey || record?.siteId || "";
  return direct || record?.mapping?.systemSoh || record?.config?.storeName || "";
}

function sessionStoreLabel(record = {}) {
  const label = formatStoreLabel(sessionStoreValue(record));
  return label === "All Sites" ? "Unknown site" : label;
}

function dashboardProfileOrgId(profile = {}) {
  return String(profile?.organizationId || profile?.orgId || profile?.tenantOrgId || "").trim();
}

function dashboardRecordOrgId(record = {}) {
  return String(record?.tenantOrgId || record?.organizationId || record?.orgId || record?.meta?.tenantOrgId || record?.meta?.organizationId || record?.meta?.orgId || "").trim();
}

function normalizeDashboardSku(value = "") {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function dashboardLiveRecordMatchesCurrentInventory(record = {}) {
  // filterDashboardRecords and the live-list hydration both enforce the active
  // organisation stamp before rendering. Inventory/site metadata hydrates later on
  // many routes and must not make a valid org-scoped live count disappear.
  const activeOrgId = String(getMcActiveOrgId() || "").trim();
  if (activeOrgId && dashboardRecordOrgId(record) === activeOrgId) return true;
  if (Number(record?.tenantProofVersion || 0) >= 2) return true;

  let detail = null;
  try { detail = JSON.parse(mcGetItem(detailStorageKeyFor(record?.sessionId)) || "null"); } catch (_) {}
  const detailLines = Array.isArray(detail?.lines) ? detail.lines : [];
  if (!detailLines.length) return false;

  const scope = record?.storeKey || record?.storeName || record?.store || detail?.meta?.storeKey || detail?.meta?.storeName || "ALL";
  const engine = loadEngineState(scope) || loadEngineState("ALL") || {};
  const inventoryRows = Array.isArray(engine?.preparedRows) && engine.preparedRows.length
    ? engine.preparedRows
    : (Array.isArray(engine?.rawRows) ? engine.rawRows : []);
  if (!inventoryRows.length) return false;

  const inventorySkus = new Set(inventoryRows.map((row) => normalizeDashboardSku(row?.sku || row?.SKU || row?.barcode || row?.id)).filter(Boolean));
  const sessionSkus = Array.from(new Set(detailLines.map((row) => normalizeDashboardSku(row?.sku || row?.SKU || row?.barcode || row?.id)).filter(Boolean)));
  if (!sessionSkus.length || !inventorySkus.size) return false;

  const overlap = sessionSkus.filter((sku) => inventorySkus.has(sku)).length;
  return overlap / sessionSkus.length >= 0.5;
}

function filterDashboardRecords(records = [], profile, adminStoreView, authoritativeOrgId = "") {
  if (!profile) return [];
  // The active organisation from the cloud-sync layer is the tenant authority.
  // Owner/admin profile objects may hydrate without organizationId/orgId, so using
  // the profile stamp here silently discarded valid records after the live listener
  // had already delivered them. Keep the profile value only as a safe fallback.
  const expectedOrgId = String(authoritativeOrgId || dashboardProfileOrgId(profile) || "").trim();
  if (!expectedOrgId) return [];

  // Dashboard isolation is deliberately stricter than the shared storage helpers.
  // A record without an explicit tenant stamp is not safe to render, and a record
  // stamped for another organization is always discarded before any metrics run.
  const tenantRecords = (Array.isArray(records) ? records : []).filter(
    (record) => dashboardRecordOrgId(record) === expectedOrgId
  );
  const currentView = adminStoreView || getAdminStoreView();
  const viewKey = currentView && currentView !== "ALL" ? storeCompareKey(currentView) : "";

  const recordMatchesKey = (item, key) => {
    if (!key) return true;
    const candidates = sessionStoreCandidates(item).map(storeCompareKey).filter(Boolean);
    if (!candidates.length) return false;
    return candidates.includes(key);
  };

  if (isAdminProfile(profile)) {
    if (!viewKey) return tenantRecords;
    return tenantRecords.filter((item) => recordMatchesKey(item, viewKey));
  }

  const allowedKeys = getProfileAllowedSiteIds(profile)
    .filter((site) => site !== "ALL")
    .map(storeCompareKey)
    .filter(Boolean);

  if (!allowedKeys.length) return [];
  const effectiveKeys = viewKey ? allowedKeys.filter((key) => key === viewKey) : allowedKeys;
  if (!effectiveKeys.length) return [];

  return tenantRecords.filter((item) => effectiveKeys.some((key) => recordMatchesKey(item, key)));
}

function uniqueSessions(records = []) {
  const seen = new Set();
  return records.filter((record) => {
    const key = record?.sessionId || `${record?.storeName || "site"}-${record?.startedAt || record?.createdAt || Math.random()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function isMorpheusLightTheme() {
  if (typeof document === "undefined") return false;
  const html = document.documentElement;
  const body = document.body;
  const classText = `${html?.className || ""} ${body?.className || ""}`.toLowerCase();
  const dataTheme = `${html?.dataset?.theme || ""} ${body?.dataset?.theme || ""} ${html?.dataset?.mcTheme || ""} ${body?.dataset?.mcTheme || ""}`.toLowerCase();
  const themeText = `${classText} ${dataTheme}`;

  // Prefer the explicit MC theme class/data flag so a global browser/app
  // dark class does not accidentally force dashboard PDF/pie rendering dark.
  if (/mc-theme-light|mc-light-theme|mc-light|\bis-light\b|data-mc-theme.*light/.test(themeText)) return true;
  if (/mc-theme-dark|mc-dark-theme|mc-dark|\bis-dark\b|data-mc-theme.*dark/.test(themeText)) return false;
  if (/light/.test(themeText)) return true;
  if (/dark/.test(themeText)) return false;

  const page = document.querySelector(".mc-workspace");
  if (page) {
    const color = window.getComputedStyle(page).backgroundColor || "";
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (match) {
      const brightness = (Number(match[1]) * 299 + Number(match[2]) * 587 + Number(match[3]) * 114) / 1000;
      return brightness > 180;
    }
  }

  try {
    const savedTheme = `${localStorage.getItem("mc-theme") || ""} ${localStorage.getItem("morpheus-theme") || ""} ${localStorage.getItem("matrixos-mc-theme") || ""}`.toLowerCase();
    if (/dark/.test(savedTheme)) return false;
    if (/light/.test(savedTheme)) return true;
  } catch (_) {
    // Ignore storage errors and keep the dark export fallback.
  }

  return false;
}

function buildConic(items = []) {
  const total = items.reduce((sum, item) => sum + Number(item.value || 0), 0);
  if (!total) return isMorpheusLightTheme() ? "#f7fdff" : "rgba(8, 20, 40, 0.9)";
  let start = 0;
  const segments = items.map((item) => {
    const end = start + (Number(item.value || 0) / total) * 360;
    const segment = `${item.color} ${start}deg ${end}deg`;
    start = end;
    return segment;
  });
  return `conic-gradient(${segments.join(", ")})`;
}



function responseRecordTimestamp(recordOrValue) {
  if (recordOrValue && typeof recordOrValue === "object") {
    const candidates = [
      recordOrValue.createdAt,
      recordOrValue.updatedAt,
      recordOrValue.submittedAt,
      recordOrValue.deliveredAt,
      recordOrValue.sentAt,
      recordOrValue.queuedAt,
      recordOrValue.timestamp,
    ];
    for (const candidate of candidates) {
      const parsed = new Date(candidate || 0).getTime();
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return NaN;
  }
  return new Date(recordOrValue || 0).getTime();
}

function matchesResponsePeriod(value, period = "today") {
  const time = responseRecordTimestamp(value);
  if (!Number.isFinite(time)) return false;
  if (period === "all") return true;

  const now = new Date();
  if (period === "today") {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return time >= start;
  }

  const days = period === "30d" ? 30 : 7;
  return time >= Date.now() - days * 24 * 60 * 60 * 1000;
}

function MorpheusCountNotificationCenter() {
  const currentProfile = useCurrentMcProfile();
  const [verificationNote, setVerificationNote] = useState("");
  const [tick, setTick] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("today");
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [lastQueueCheck, setLastQueueCheck] = useState("");
  const [selectedResponseId, setSelectedResponseId] = useState("");
  const [intelligenceTab, setIntelligenceTab] = useState("response");

  useEffect(() => {
    const bump = () => setTick((value) => value + 1);
    window.addEventListener(MC_NOTIFICATION_EVENT, bump);
    window.addEventListener(MC_RESPONSE_EVENT, bump);
    window.addEventListener("storage", bump);
    window.addEventListener("mc-cloud-sync", bump);
    window.addEventListener("mc-cloud-sync-ready", bump);
    return () => {
      window.removeEventListener(MC_NOTIFICATION_EVENT, bump);
      window.removeEventListener(MC_RESPONSE_EVENT, bump);
      window.removeEventListener("storage", bump);
      window.removeEventListener("mc-cloud-sync", bump);
      window.removeEventListener("mc-cloud-sync-ready", bump);
    };
  }, []);

  const runDeliveryQueue = async () => {
    if (isProcessingQueue) return;
    setIsProcessingQueue(true);
    try {
      await processDeliveryQueue("Response Intelligence");
      setLastQueueCheck(new Date().toISOString());
      setTick((value) => value + 1);
    } finally {
      setIsProcessingQueue(false);
    }
  };

  useEffect(() => {
    let active = true;
    const autoProcess = async () => {
      try {
        await processDeliveryQueue("Response Intelligence · automatic");
        if (active) {
          setLastQueueCheck(new Date().toISOString());
          setTick((value) => value + 1);
        }
      } catch (_) {}
    };
    const timer = window.setTimeout(autoProcess, 400);
    const interval = window.setInterval(autoProcess, 30000);
    return () => { active = false; window.clearTimeout(timer); window.clearInterval(interval); };
  }, []);

  const notifications = useMemo(() => loadResponseNotifications(), [tick]);
  const responseEvents = useMemo(() => loadResponseEvents(), [tick]);
  const periodResponseEvents = useMemo(() => responseEvents
    .filter((item) => matchesResponsePeriod(item, periodFilter))
    .sort((a, b) => responseRecordTimestamp(b) - responseRecordTimestamp(a)), [responseEvents, periodFilter]);
  const selectedResponseEvent = periodResponseEvents.find((item) => item.id === selectedResponseId) || periodResponseEvents[0] || null;
  const externalNotifications = useMemo(() => notifications.filter((item) => String(item?.channel || "workspace").toLowerCase() !== "workspace"), [notifications]);
  const periodNotifications = useMemo(() => externalNotifications
    .filter((item) => matchesResponsePeriod(item, periodFilter))
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)), [externalNotifications, periodFilter]);
  const filtered = useMemo(() => periodNotifications.filter((item) => {
    const status = String(item?.status || "queued").toLowerCase();
    const channel = String(item?.channel || "workspace").toLowerCase();
    return (statusFilter === "all" || status === statusFilter) && (channelFilter === "all" || channel === channelFilter);
  }), [periodNotifications, statusFilter, channelFilter]);
  const selected = periodNotifications.find((item) => item.id === selectedId) || filtered[0] || null;
  const summary = buildNotificationSummary(periodNotifications);
  const needsAttention = periodNotifications.filter((item) => ["failed", "queued", "sending", "retrying"].includes(String(item?.status || "").toLowerCase()));
  const preparedCount = periodNotifications.filter((item) => String(item?.status || "").toLowerCase() === "prepared").length;
  const deliveredCount = summary.sent.length + summary.delivered.length + summary.read.length;
  const confirmableCount = periodNotifications.filter((item) => !["prepared", "skipped"].includes(String(item?.status || "").toLowerCase())).length;
  const successfulRate = confirmableCount ? Math.round((deliveredCount / confirmableCount) * 100) : 100;
  const channelCounts = ["email", "whatsapp"].map((channel) => {
    const rows = periodNotifications.filter((item) => String(item?.channel || "workspace").toLowerCase() === channel);
    const delivered = rows.filter((item) => ["sent", "delivered", "read"].includes(String(item?.status || "").toLowerCase())).length;
    const pending = rows.filter((item) => ["queued", "sending", "retrying"].includes(String(item?.status || "").toLowerCase())).length;
    const prepared = rows.filter((item) => String(item?.status || "").toLowerCase() === "prepared").length;
    const skipped = rows.filter((item) => String(item?.status || "").toLowerCase() === "skipped").length;
    const failed = rows.filter((item) => String(item?.status || "").toLowerCase() === "failed").length;
    return { channel, total: rows.length, delivered, pending, prepared, skipped, failed };
  });

  const selectAttention = (item) => {
    setSelectedId(item.id);
    setStatusFilter("all");
    setChannelFilter("all");
  };

  return (
    <div className="mc-workspace mc-deliveryIntel mc-tabPage">

      <McPageHeader activeKey="notifications" />

      <nav className="mc-intelligenceTabs" aria-label="Response Intelligence sections">
        <button type="button" className={intelligenceTab === "response" ? "is-active" : ""} onClick={() => setIntelligenceTab("response")}>
          <span>Response</span>
          <small>Delivery status and channel health</small>
        </button>
        <button type="button" className={intelligenceTab === "movement" ? "is-active" : ""} onClick={() => setIntelligenceTab("movement")}>
          <span>Movement</span>
          <small>Corrections, exposure changes and resolution</small>
        </button>
      </nav>

      <section className="mc-deliveryIntel__controlBar" aria-label="Response intelligence controls">
        <label className="mc-deliveryIntel__period">
          <span>Period</span>
          <select value={periodFilter} onChange={(e) => { setPeriodFilter(e.target.value); setSelectedId(""); }}>
            <option value="today">Today</option><option value="7d">Last 7 days</option><option value="30d">Last 30 days</option><option value="all">All time</option>
          </select>
        </label>
        {intelligenceTab === "response" ? <div className="mc-deliveryIntel__retryControl">
          <button type="button" className="mc-deliveryIntel__processBtn" onClick={runDeliveryQueue} disabled={isProcessingQueue} title="Check queued and failed deliveries now">
            <i className={isProcessingQueue ? "is-spinning" : ""} />
            {isProcessingQueue ? "Checking deliveries" : "Retry deliveries now"}
          </button>
          <small>{lastQueueCheck ? `Last checked ${formatDateTime(lastQueueCheck)}` : "Automatic delivery checks are active"}</small>
        </div> : <div className="mc-deliveryIntel__movementHint"><strong>Operational history</strong><small>Showing corrections and response movement for the selected period.</small></div>}
      </section>

      {intelligenceTab === "response" ? <>
      <section className="mc-deliveryIntel__overview">
        <article className="mc-deliveryIntel__score">
          <span>Delivery health</span>
          <strong>{successfulRate}%</strong>
          <small>{deliveredCount} of {confirmableCount} responses delivered{preparedCount ? ` · ${preparedCount} prepared` : ""}</small>
          <div className="mc-deliveryIntel__bar"><i style={{ width: `${successfulRate}%` }} /></div>
        </article>
        <div className="mc-deliveryIntel__channels">
          {channelCounts.map((item) => <button key={item.channel} type="button" onClick={() => setChannelFilter(item.channel)} className={channelFilter === item.channel ? "is-active" : ""}>
            <span>{item.channel}</span>
            <strong>{item.total}</strong>
            <small><b>{item.delivered}</b> sent · {item.pending} pending{item.prepared ? ` · ${item.prepared} prepared` : ""}{item.skipped ? ` · ${item.skipped} skipped` : ""}{item.failed ? ` · ${item.failed} failed` : ""}</small>
          </button>)}
        </div>
        <article className={`mc-deliveryIntel__attentionCard ${needsAttention.length ? "has-items" : ""}`}>
          <span>Needs attention</span>
          <strong>{needsAttention.length}</strong>
          <small>{needsAttention.length ? "Queued, sending or failed" : "Everything reached its destination"}</small>
        </article>
      </section>

      {needsAttention.length ? <section className="mc-deliveryIntel__attentionStrip">
        <div className="mc-deliveryIntel__sectionTitle"><div><span>Act now</span><h2>Responses requiring attention</h2></div><small>{needsAttention.length} item{needsAttention.length === 1 ? "" : "s"}</small></div>
        <div className="mc-deliveryIntel__attentionGrid">
          {needsAttention.slice(0, 4).map((item) => <button key={item.id} type="button" onClick={() => selectAttention(item)}>
            <div><b className={`is-${String(item.status || "queued").toLowerCase()}`}>{notificationStatusLabel(item.status)}</b><span>{String(item.channel || "workspace").toUpperCase()}</span></div>
            <strong>{item.siteName} · {item.sectionName}</strong>
            <small>{item.recipientName || item.recipient} · {formatDateTime(item.createdAt)}</small>
          </button>)}
        </div>
      </section> : null}

      <section className="mc-deliveryIntel__toolbar">
        <div>
          <button type="button" className={statusFilter === "all" ? "is-active" : ""} onClick={() => setStatusFilter("all")}>All</button>
          <button type="button" className={statusFilter === "sent" ? "is-active" : ""} onClick={() => setStatusFilter("sent")}>Sent</button>
          <button type="button" className={statusFilter === "delivered" ? "is-active" : ""} onClick={() => setStatusFilter("delivered")}>Delivered</button>
          <button type="button" className={statusFilter === "skipped" ? "is-active" : ""} onClick={() => setStatusFilter("skipped")}>Skipped</button>
          <button type="button" className={statusFilter === "queued" ? "is-active" : ""} onClick={() => setStatusFilter("queued")}>Queued</button>
          <button type="button" className={statusFilter === "prepared" ? "is-active" : ""} onClick={() => setStatusFilter("prepared")}>Prepared</button>
          <button type="button" className={statusFilter === "failed" ? "is-active" : ""} onClick={() => setStatusFilter("failed")}>Failed</button>
        </div>
        <label>Channel<select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}><option value="all">All channels</option><option value="email">Email</option><option value="whatsapp">WhatsApp</option></select></label>
      </section>

      <section className="mc-deliveryIntel__workspace">
        <div className="mc-deliveryIntel__feed">
          <div className="mc-deliveryIntel__sectionTitle"><div><span>Latest activity</span><h2>{filtered.length} response deliveries</h2></div></div>
          <div className="mc-deliveryIntel__feedScroll">
            {filtered.map((item) => <button key={item.id} type="button" className={`mc-deliveryIntel__feedItem ${selected?.id === item.id ? "is-selected" : ""}`} onClick={() => setSelectedId(item.id)}>
              <i className={`is-${String(item.status || "queued").toLowerCase()}`} />
              <div className="mc-deliveryIntel__feedMain"><div><strong>{item.siteName} · {item.sectionName}</strong><span>{String(item.channel || "workspace").toUpperCase()}</span></div><p>{item.subject}</p><small>{item.recipientName || item.recipient}</small></div>
              <div className="mc-deliveryIntel__feedMeta"><b>{notificationStatusLabel(item.status)}</b><time>{formatDateTime(item.createdAt)}</time></div>
            </button>)}
            {!filtered.length ? <div className="mc-notificationCenter__empty">No response deliveries match these filters.</div> : null}
          </div>
        </div>

        <aside className="mc-deliveryIntel__detail">
          {selected ? <>
            <div className="mc-deliveryIntel__detailHead"><div><span>Selected response</span><h2>{selected.siteName} · {selected.sectionName}</h2></div><b className={`is-${String(selected.status || "queued").toLowerCase()}`}>{notificationStatusLabel(selected.status)}</b></div>
            <div className="mc-deliveryIntel__route">
              <article><span>Channel</span><strong>{String(selected.channel || "workspace").toUpperCase()}</strong></article>
              <i>→</i>
              <article><span>Recipient</span><strong>{selected.recipientName || selected.recipient}</strong><small>{selected.recipient}</small></article>
            </div>
            <div className="mc-deliveryIntel__timeline">
              <span>Response journey</span>
              <div className="is-complete"><i /><div><strong>Response created</strong><small>{formatDateTime(selected.createdAt)}</small></div></div>
              <div className={["sending","prepared","sent","delivered","read"].includes(String(selected.status || "").toLowerCase()) ? "is-complete" : String(selected.status || "").toLowerCase() === "failed" ? "is-failed" : String(selected.status || "").toLowerCase() === "skipped" ? "is-complete" : "is-current"}><i /><div><strong>{String(selected.status || "").toLowerCase() === "skipped" ? "Skipped" : "Queued"}</strong><small>{String(selected.status || "").toLowerCase() === "skipped" ? "No WhatsApp number is configured for this recipient" : selected.queuedAt ? formatDateTime(selected.queuedAt) : "Ready for processing"}</small></div></div>
              {String(selected.status || "").toLowerCase() === "prepared" ? <div className="is-current"><i /><div><strong>Prepared</strong><small>Payload validated. Waiting for Meta credentials before live sending.</small></div></div> : <>
                <div className={["sent","delivered","read"].includes(String(selected.status || "").toLowerCase()) ? "is-complete" : String(selected.status || "").toLowerCase() === "sending" ? "is-current" : String(selected.status || "").toLowerCase() === "failed" ? "is-failed" : "is-pending"}><i /><div><strong>Sending</strong><small>{selected.sendingAt ? formatDateTime(selected.sendingAt) : String(selected.status || "").toLowerCase() === "failed" ? "Send attempt failed" : "Waiting to send"}</small></div></div>
                <div className={["sent","delivered","read"].includes(String(selected.status || "").toLowerCase()) ? "is-complete" : String(selected.status || "").toLowerCase() === "failed" ? "is-failed" : "is-pending"}><i /><div><strong>{String(selected.status || "").toLowerCase() === "failed" ? "Delivery failed" : String(selected.status || "").toLowerCase() === "sent" ? "Sent" : "Delivered"}</strong><small>{selected.sentAt ? formatDateTime(selected.sentAt) : selected.deliveredAt ? formatDateTime(selected.deliveredAt) : String(selected.status || "").toLowerCase() === "failed" ? (selected.providerErrorMessage || "Retry when ready") : "Waiting for provider confirmation"}</small></div></div>
              </>}
            </div>
            {String(selected.channel || "").toLowerCase() === "whatsapp" && String(selected.status || "").toLowerCase() === "failed" ? (
              <section className="mc-deliveryIntel__diagnostic">
                <span>Meta delivery diagnostic</span>
                <strong>{selected.providerErrorCode ? `Error ${selected.providerErrorCode}` : "WhatsApp provider error"}</strong>
                <p>{selected.providerErrorMessage || "The provider rejected this delivery attempt."}</p>
                <div>
                  <small>Intended recipient</small><b>{selected.intendedRecipient || selected.recipient || "—"}</b>
                  <small>Actual destination</small><b>{selected.deliveredTo || selected.recipient || "—"}</b>
                  <small>Mode</small><b>{selected.sandboxMode ? "Sandbox" : "Production"}</b>
                  {selected.providerHttpStatus ? <><small>HTTP status</small><b>{selected.providerHttpStatus}</b></> : null}
                </div>
              </section>
            ) : null}
            {String(selected.channel || "").toLowerCase() === "email" ? (
              <details className="mc-deliveryIntel__message mc-deliveryIntel__message--email">
                <summary>View email sent</summary>
                {selected.messageHtml ? (
                  <iframe title="Email sent preview" srcDoc={selected.messageHtml} sandbox="" />
                ) : (
                  <pre>{selected.message}</pre>
                )}
              </details>
            ) : (
              <details className="mc-deliveryIntel__message">
                <summary>View WhatsApp message</summary>
                <pre>{selected.message}</pre>
              </details>
            )}
            {["queued","failed"].includes(String(selected.status || "").toLowerCase()) ? <button className="mc-workspace__btn" disabled={isProcessingQueue} onClick={async () => { if (String(selected.status || "").toLowerCase() === "failed") retryNotification(selected.id, "Operator"); await runDeliveryQueue(); }}>{String(selected.status || "").toLowerCase() === "failed" ? "Retry response" : "Send now"}</button> : null}
            {String(selected.status || "").toLowerCase() === "prepared" ? <div className="mc-deliveryIntel__preparedNote"><strong>WhatsApp is ready.</strong><span>It will switch to live sending automatically when Meta credentials are connected.</span></div> : null}
          </> : <div className="mc-notificationCenter__empty">Select a response delivery.</div>}
        </aside>
      </section>

      </> : null}

      {intelligenceTab === "movement" ? <section className="mc-responseMovement">
        <div className="mc-deliveryIntel__sectionTitle">
          <div><span>Response movement</span><h2>Operational response history</h2></div>
          <small>{periodResponseEvents.length} response{periodResponseEvents.length === 1 ? "" : "s"} in this period</small>
        </div>
        <div className="mc-responseMovement__layout">
          <div className="mc-responseMovement__list">
            {periodResponseEvents.map((event) => {
              const corrections = Array.isArray(event.corrections) ? event.corrections : [];
              return <button key={event.id} type="button" className={selectedResponseEvent?.id === event.id ? "is-selected" : ""} onClick={() => setSelectedResponseId(event.id)}>
                <div><strong>{event.siteName} · {event.sectionName}</strong><b className={`is-${String(event.status || "open").toLowerCase()}`}>{responseStatusLabel(event.status)}</b></div>
                <p>{responseSeverityLabel(event.severity)} response · {formatCurrency(event.exposure)}</p>
                <small>{event.countedBy || "Unknown user"} · {corrections.length} correction{corrections.length === 1 ? "" : "s"}</small>
              </button>;
            })}
            {!periodResponseEvents.length ? <div className="mc-notificationCenter__empty">No operational responses match this period. Delivery records are kept separately above.</div> : null}
          </div>
          <aside className="mc-responseMovement__detail">
            {selectedResponseEvent ? <>
              <div className="mc-responseMovement__head">
                <div><span>Selected response</span><h3>{selectedResponseEvent.siteName} · {selectedResponseEvent.sectionName}</h3></div>
                <b className={`is-${String(selectedResponseEvent.status || "open").toLowerCase()}`}>{responseStatusLabel(selectedResponseEvent.status)}</b>
              </div>
              <div className="mc-responseMovement__metrics">
                <span><small>Current exposure</small><strong>{formatCurrency(selectedResponseEvent.exposure)}</strong></span>
                <span><small>Products remaining</small><strong>{Number(selectedResponseEvent.flaggedProducts || 0)}</strong></span>
                <span><small>Exposure corrected</small><strong>{formatCurrency(Math.abs(Number(selectedResponseEvent.recoveredValueTotal || 0)))}</strong></span>
              </div>
              {String(selectedResponseEvent.status || "").toLowerCase() === "awaiting_verification" ? <div className="mc-responseMovement__verification">
                <div>
                  <span>Verification pending</span>
                  <strong>{selectedResponseEvent.verificationRole === "custom_profile" ? (selectedResponseEvent.verificationProfile || "Custom profile") : String(selectedResponseEvent.verificationRole || "operations_manager").replace(/_/g, " ")}</strong>
                  <small>A correction was submitted. Approve it to resolve the response, or reject it to keep the response open.</small>
                </div>
                <textarea value={verificationNote} onChange={(e) => setVerificationNote(e.target.value)} placeholder="Optional verification note" />
                <div className="mc-responseMovement__verificationActions">
                  <button type="button" className="is-reject" onClick={() => { verifyResponseCorrection(selectedResponseEvent.id, "reject", currentProfile?.name || currentProfile?.fullName || "Operator", verificationNote); setVerificationNote(""); setTick((v) => v + 1); }}>Reject correction</button>
                  <button type="button" className="is-approve" onClick={() => { verifyResponseCorrection(selectedResponseEvent.id, "approve", currentProfile?.name || currentProfile?.fullName || "Operator", verificationNote); setVerificationNote(""); setTick((v) => v + 1); }}>Approve & resolve</button>
                </div>
              </div> : null}
              <div className="mc-responseMovement__timeline">
                {(Array.isArray(selectedResponseEvent.timeline) ? selectedResponseEvent.timeline : []).map((entry) => <div key={entry.id || `${entry.timestamp}-${entry.action}`}>
                  <i /><p><strong>{entry.action}</strong><small>{formatDateTime(entry.timestamp)} · {entry.by || "System"}</small></p>
                </div>)}
              </div>
              {(Array.isArray(selectedResponseEvent.corrections) ? selectedResponseEvent.corrections : []).length ? <div className="mc-responseMovement__corrections">
                <h4>Correction history</h4>
                {selectedResponseEvent.corrections.map((revision, index) => <details key={revision.id || index}>
                  <summary>Version {index + 2} · {revision.correctedBy || "Unknown user"} · {formatDateTime(revision.submittedAt)}</summary>
                  <div className="mc-responseMovement__correctionMeta">
                    <span><small>Role</small><strong>{revision.correctedByRole || "Morpheus profile"}</strong></span>
                    <span><small>Reason</small><strong>{revision.reason || "Not recorded"}</strong></span>
                    <span><small>Original</small><strong>{formatCurrency(revision.originalExposure)}</strong></span>
                    <span><small>Current</small><strong>{formatCurrency(revision.currentExposure)}</strong></span>
                    <span><small>Exposure movement</small><strong>{formatCurrency(Math.abs(Number(revision.recoveredValue || 0)))}</strong></span>
                  </div>
                  <div className="mc-responseMovement__products">
                    {(Array.isArray(revision.changedProducts) ? revision.changedProducts : []).map((product, productIndex) => <div key={product.productKey || product.id || productIndex}>
                      <strong>{product.product || product.name || "Product"}</strong>
                      <small>Counted: {product.originalCounted ?? product.original?.counted ?? "–"} → {product.correctedCounted ?? product.corrected?.counted ?? "–"}</small>
                      <small>Waste: {product.originalWaste ?? product.original?.waste ?? "–"} → {product.correctedWaste ?? product.corrected?.waste ?? "–"}</small>
                    </div>)}
                  </div>
                </details>)}
              </div> : <div className="mc-responseMovement__empty">No corrections submitted yet.</div>}
            </> : <div className="mc-notificationCenter__empty">Select an operational response.</div>}
          </aside>
        </div>
      </section> : null}
      <McPageFooter />
    </div>
  );
}

function MorpheusCountResponseRules() {
  const [tick, setTick] = useState(0);
  const rules = useMemo(() => loadNotificationRules(), [tick]);
  const notifications = useMemo(() => loadResponseNotifications(), [tick]);
  const notificationSummary = useMemo(() => buildNotificationSummary(notifications), [notifications]);
  const [notificationStatusFilter, setNotificationStatusFilter] = useState("all");
  const [notificationChannelFilter, setNotificationChannelFilter] = useState("all");
  const [emailConfig, setEmailConfig] = useState(() => loadEmailDeliveryConfig());
  const [emailDeliveryStatus, setEmailDeliveryStatus] = useState("");
  const [whatsappConfig, setWhatsappConfig] = useState(() => loadWhatsappDeliveryConfig());
  const [whatsappDeliveryStatus, setWhatsappDeliveryStatus] = useState("");
  const [rulesTab, setRulesTab] = useState("rules");
  const [recipientRules, setRecipientRules] = useState(() => loadResponseRecipientRules());
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [editingRecipientId, setEditingRecipientId] = useState("");
  const [recipientDraft, setRecipientDraft] = useState(() => createResponseRecipientRule());
  const [recipientNotice, setRecipientNotice] = useState(null);
  const [recipientDeleteTarget, setRecipientDeleteTarget] = useState(null);
  const sites = useMemo(() => {
    try {
      return loadSites();
    } catch (_) {
      return [];
    }
  }, [tick]);
  const filteredNotifications = useMemo(() => {
    return notifications.filter((item) => {
      const status = String(item?.status || "queued").toLowerCase();
      const channel = String(item?.channel || "workspace").toLowerCase();
      const statusOk = notificationStatusFilter === "all" || status === notificationStatusFilter;
      const channelOk = notificationChannelFilter === "all" || channel === notificationChannelFilter;
      return statusOk && channelOk;
    });
  }, [notifications, notificationStatusFilter, notificationChannelFilter]);

  useEffect(() => {
    const bump = () => setTick((value) => value + 1);
    window.addEventListener(MC_NOTIFICATION_EVENT, bump);
    window.addEventListener(MC_RESPONSE_EVENT, bump);
    window.addEventListener("storage", bump);
    window.addEventListener("mc-cloud-sync", bump);
    window.addEventListener("mc-cloud-sync-ready", bump);
    return () => {
      window.removeEventListener(MC_NOTIFICATION_EVENT, bump);
      window.removeEventListener(MC_RESPONSE_EVENT, bump);
      window.removeEventListener("storage", bump);
      window.removeEventListener("mc-cloud-sync", bump);
      window.removeEventListener("mc-cloud-sync-ready", bump);
    };
  }, []);

  const updateRule = (ruleId, patch) => {
    const next = rules.map((rule) => rule.id === ruleId ? { ...rule, ...patch } : rule);
    saveNotificationRules(next);
    setTick((value) => value + 1);
  };

  const toggleChannel = (rule, channel) => {
    const channels = Array.isArray(rule.channels) ? rule.channels : [];
    const nextChannels = channels.includes(channel) ? channels.filter((item) => item !== channel) : [...channels, channel];
    updateRule(rule.id, { channels: nextChannels.length ? nextChannels : ["workspace"] });
  };

  const toggleSeverity = (rule, severity) => {
    const severities = Array.isArray(rule.severities) ? rule.severities : [];
    const nextSeverities = severities.includes(severity) ? severities.filter((item) => item !== severity) : [...severities, severity];
    updateRule(rule.id, { severities: nextSeverities.length ? nextSeverities : [severity] });
  };

  const addRule = () => {
    const nextRule = {
      id: `rule-custom-${Date.now()}`,
      name: "Custom response rule",
      active: true,
      trigger: "severity",
      severities: ["critical"],
      minExposure: 0,
      minFlaggedProducts: 0,
      recipients: ["Manager"],
      channels: ["workspace"],
      deliveryMode: "immediate",
    };
    saveNotificationRules([nextRule, ...rules]);
    setTick((value) => value + 1);
  };


  const refreshRecipientRules = () => {
    setRecipientRules(loadResponseRecipientRules());
    setTick((value) => value + 1);
  };

  const openRecipientCreate = () => {
    setRecipientNotice(null);
    setEditingRecipientId("");
    setRecipientDraft(createResponseRecipientRule());
    setRecipientModalOpen(true);
  };

  const openRecipientEdit = (rule) => {
    setRecipientNotice(null);
    setEditingRecipientId(rule.id);
    setRecipientDraft(createResponseRecipientRule(rule));
    setRecipientModalOpen(true);
  };

  const closeRecipientModal = () => {
    setRecipientModalOpen(false);
    setEditingRecipientId("");
    setRecipientDraft(createResponseRecipientRule());
    setRecipientNotice(null);
  };

  const toggleRecipientRole = (roleId) => {
    const current = Array.isArray(recipientDraft.roles) ? recipientDraft.roles : [];
    const nextRoles = current.includes(roleId) ? current.filter((item) => item !== roleId) : [...current, roleId];
    setRecipientDraft((draft) => ({ ...draft, roles: nextRoles }));
  };

  const toggleRecipientChannel = (channelId) => {
    const current = Array.isArray(recipientDraft.channels) ? recipientDraft.channels : [];
    const nextChannels = current.includes(channelId) ? current.filter((item) => item !== channelId) : [...current, channelId];
    setRecipientDraft((draft) => ({ ...draft, channels: nextChannels.length ? nextChannels : ["workspace"] }));
  };

  const saveRecipientDraft = () => {
    const roles = Array.isArray(recipientDraft.roles) ? recipientDraft.roles.filter(Boolean) : [];
    const emails = parseRecipientEmails(recipientDraft.emailText || recipientDraft.emails || []);
    const phones = parseRecipientPhones(recipientDraft.phoneText || recipientDraft.phones || []);
    const channels = Array.isArray(recipientDraft.channels) && recipientDraft.channels.length ? recipientDraft.channels : ["workspace"];
    if (!roles.length && !emails.length && !phones.length) {
      setRecipientNotice({
        type: "warning",
        title: "Select a recipient",
        message: "Choose at least one role or add one email address before saving this recipient rule.",
      });
      return;
    }
    const nextRule = createResponseRecipientRule({
      ...recipientDraft,
      roles,
      emails,
      emailText: emails.join("\n"),
      severity: recipientDraft.severity || "critical",
      siteId: recipientDraft.siteId || "all",
      siteName: recipientDraft.siteId === "all" ? "All Sites" : (recipientDraft.siteName || sites.find((site) => site.siteId === recipientDraft.siteId)?.name || "Selected site"),
    });
    try {
      if (editingRecipientId) {
        updateResponseRecipientRule(editingRecipientId, nextRule);
      } else {
        saveResponseRecipientRules([nextRule, ...loadResponseRecipientRules()]);
      }
      refreshRecipientRules();
      closeRecipientModal();
    } catch (error) {
      setRecipientNotice({
        type: "danger",
        title: "Recipient rule could not save",
        message: error?.message || "Browser storage is full. Clear Morpheus cache and try again.",
      });
    }
  };

  const removeRecipientRule = (rule) => {
    setRecipientDeleteTarget(rule);
  };

  const confirmRemoveRecipientRule = () => {
    if (!recipientDeleteTarget?.id) return;
    try {
      deleteResponseRecipientRule(recipientDeleteTarget.id);
      setRecipientDeleteTarget(null);
      refreshRecipientRules();
    } catch (error) {
      setRecipientDeleteTarget(null);
      setRecipientNotice({
        type: "danger",
        title: "Recipient rule could not be deleted",
        message: error?.message || "Browser storage is full. Clear Morpheus cache and try again.",
      });
      setRecipientModalOpen(true);
    }
  };

  const setNotificationStatus = (notificationId, status) => {
    updateNotificationStatus(notificationId, status, "Morpheus");
    setTick((value) => value + 1);
  };

  const retryQueuedNotification = (notificationId) => {
    retryNotification(notificationId, "Morpheus");
    setTick((value) => value + 1);
  };

  const updateEmailConfig = (patch) => {
    const next = saveEmailDeliveryConfig({ ...emailConfig, ...patch });
    setEmailConfig(next);
  };

  const processEmailQueue = async () => {
    setEmailDeliveryStatus("Processing email queue...");
    const results = await processQueuedEmailNotifications(emailConfig, "Morpheus");
    const delivered = results.filter((item) => item?.ok).length;
    const failed = results.length - delivered;
    setEmailDeliveryStatus(results.length ? `${delivered} delivered · ${failed} failed` : "No queued email notifications");
    setTick((value) => value + 1);
  };

  const updateWhatsappConfig = (patch) => {
    const next = saveWhatsappDeliveryConfig({ ...whatsappConfig, ...patch });
    setWhatsappConfig(next);
  };

  const processWhatsappQueue = async () => {
    setWhatsappDeliveryStatus("Processing WhatsApp queue...");
    const results = await processQueuedWhatsappNotifications(whatsappConfig, "Morpheus");
    const delivered = results.filter((item) => item?.ok).length;
    const failed = results.length - delivered;
    setWhatsappDeliveryStatus(results.length ? `${delivered} delivered · ${failed} failed` : "No queued WhatsApp notifications");
    setTick((value) => value + 1);
  };

  return (
    <div className="mc-workspace mc-responseRules mc-tabPage">
      <div className="mc-workspace__grid" />
      <div className="mc-workspace__ambient mc-workspace__ambient--one" />
      <div className="mc-workspace__ambient mc-workspace__ambient--two" />

      <McPageHeader
        activeKey="response-rules"
        actions={(
          <div className="mc-responseRules__actionBar" aria-label="Response rule actions">
            <button className="mc-workspace__btn mc-workspace__btn--primary" type="button" onClick={addRule}>+ Add Rule</button>
            <Link className="mc-workspace__btn mc-workspace__btn--ghost" to="/morpheus-count/workspace#responses">Back to Responses</Link>
          </div>
        )}
      />

      
      <div className="mc-responseRules__subtabs" style={{display:"flex",gap:12,marginBottom:20}}>
        <button className={`mc-workspace__btn ${rulesTab === "rules" ? "mc-workspace__btn--primary" : "mc-workspace__btn--ghost"}`} type="button" onClick={() => setRulesTab("rules")}>Rules</button>
        <button className={`mc-workspace__btn ${rulesTab === "recipients" ? "mc-workspace__btn--primary" : "mc-workspace__btn--ghost"}`} type="button" onClick={() => setRulesTab("recipients")}>Recipients</button>
      </div>

      {rulesTab === "recipients" ? (
        <section className="mc-responseRules__recipientPanel">
          <article className="mc-responseCenter__panel mc-responseRules__recipientBuilder">
            <div className="mc-responseCenter__panelHead">
              <div><span>Recipient Engine</span><h3>Response recipients</h3></div>
              <button
                className="mc-workspace__btn mc-workspace__btn--primary"
                type="button"
                onClick={openRecipientCreate}
              >
                + Add Recipient Rule
              </button>
            </div>
            {recipientRules.length ? (
              <div className="mc-responseRules__recipientGrid">
                {recipientRules.map((rule) => {
                  const roleLabels = (rule.roles || []).map((roleId) => RECIPIENT_ROLE_OPTIONS.find((item) => item.id === roleId)?.label || roleId);
                  const channelLabels = (rule.channels || []).map((channelId) => RECIPIENT_CHANNEL_OPTIONS.find((item) => item.id === channelId)?.label || channelId);
                  return (
                    <article key={rule.id} className="mc-responseRules__recipientCard">
                      <div className="mc-responseRules__recipientTop">
                        <div>
                          <span className={`mc-responseRules__severityPill is-${String(rule.severity || "critical").toLowerCase()}`}>{responseSeverityLabel(rule.severity)}</span>
                          <h4>{rule.siteName || rule.site || "All Sites"}</h4>
                        </div>
                        <label className="mc-responseRules__switch">
                          <input type="checkbox" checked={rule.active !== false} onChange={(event) => {
                            updateResponseRecipientRule(rule.id, { active: event.target.checked });
                            refreshRecipientRules();
                          }} />
                          <span>{rule.active !== false ? "Active" : "Disabled"}</span>
                        </label>
                      </div>
                      <div className="mc-responseRules__recipientColumns">
                        <div>
                          <span>Roles</span>
                          {roleLabels.length ? roleLabels.map((label) => <small key={label}>• {label}</small>) : <small>No role recipients</small>}
                        </div>
                        <div>
                          <span>Channels</span>
                          {channelLabels.length ? channelLabels.map((label) => <small key={label}>• {label}</small>) : <small>Workspace only</small>}
                        </div>
                        <div>
                          <span>Emails</span>
                          {(rule.emails || []).length ? (rule.emails || []).map((email) => <small key={email}>• {email}</small>) : <small>No custom emails</small>}
                        </div>
                        <div>
                          <span>WhatsApp Numbers</span>
                          {(rule.phones || []).length ? (rule.phones || []).map((phone) => <small key={phone}>• {phone}</small>) : <small>No WhatsApp numbers</small>}
                        </div>
                      </div>
                      <div className="mc-responseRules__recipientActions">
                        <button type="button" onClick={() => openRecipientEdit(rule)}>Edit</button>
                        <button type="button" onClick={() => removeRecipientRule(rule)}>Delete</button>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : <p>No recipient rules configured yet.</p>}
          </article>

          {recipientModalOpen ? (
            <div className="mc-responseRules__modalBackdrop" role="dialog" aria-modal="true">
              <div className="mc-responseRules__modal">
                <div className="mc-responseCenter__panelHead">
                  <div><span>Recipient Rule</span><h3>{editingRecipientId ? "Edit Recipient Rule" : "Create Recipient Rule"}</h3></div>
                  <button className="mc-workspace__btn mc-workspace__btn--ghost" type="button" onClick={closeRecipientModal}>Close</button>
                </div>

                {recipientNotice ? (
                  <div className={`mc-responseRules__notice is-${recipientNotice.type || "info"}`}>
                    <strong>{recipientNotice.title}</strong>
                    <span>{recipientNotice.message}</span>
                  </div>
                ) : null}

                <div className="mc-responseRules__modalGrid">
                  <label className="mc-responseRules__field">
                    <span>Severity</span>
                    <select value={recipientDraft.severity || "critical"} onChange={(event) => setRecipientDraft((draft) => ({ ...draft, severity: event.target.value }))}>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </label>

                  <label className="mc-responseRules__field">
                    <span>Applies To</span>
                    <select value={recipientDraft.siteId || "all"} onChange={(event) => {
                      const siteId = event.target.value;
                      const site = sites.find((item) => item.siteId === siteId);
                      setRecipientDraft((draft) => ({ ...draft, siteId, siteName: siteId === "all" ? "All Sites" : (site?.name || siteId) }));
                    }}>
                      <option value="all">All Sites</option>
                      {sites.map((site) => <option key={site.siteId} value={site.siteId}>{site.name}</option>)}
                    </select>
                  </label>
                </div>

                <div className="mc-responseRules__rolePicker">
                  <span>Notify Roles</span>
                  <div>
                    {RECIPIENT_ROLE_OPTIONS.map((role) => (
                      <label key={role.id}>
                        <input type="checkbox" checked={(recipientDraft.roles || []).includes(role.id)} onChange={() => toggleRecipientRole(role.id)} />
                        <span>{role.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <label className="mc-responseRules__field">
                  <span>Additional Email Addresses</span>
                  <textarea
                    value={recipientDraft.emailText ?? (recipientDraft.emails || []).join("\n")}
                    onChange={(event) => setRecipientDraft((draft) => ({ ...draft, emailText: event.target.value, emails: parseRecipientEmails(event.target.value) }))}
                    placeholder={"ops@company.com\nowner@company.com"}
                    rows={5}
                  />
                </label>

                <div className="mc-responseRules__modalActions">
                  <button className="mc-workspace__btn mc-workspace__btn--ghost" type="button" onClick={closeRecipientModal}>Cancel</button>
                  <button className="mc-workspace__btn mc-workspace__btn--primary" type="button" onClick={saveRecipientDraft}>Save Rule</button>
                </div>
              </div>
            </div>
          ) : null}

          {recipientDeleteTarget ? (
            <div className="mc-responseRules__modalBackdrop" role="dialog" aria-modal="true">
              <div className="mc-responseRules__confirmModal">
                <div className="mc-responseCenter__panelHead">
                  <div><span>Delete Recipient Rule</span><h3>Remove this recipient rule?</h3></div>
                </div>
                <p>
                  This affects future response events only. Existing response events and notification history will not be changed.
                </p>
                <div className="mc-responseRules__recipientDeleteSummary">
                  <strong>{responseSeverityLabel(recipientDeleteTarget.severity)}</strong>
                  <span>{recipientDeleteTarget.siteName || "All Sites"}</span>
                </div>
                <div className="mc-responseRules__modalActions">
                  <button className="mc-workspace__btn mc-workspace__btn--ghost" type="button" onClick={() => setRecipientDeleteTarget(null)}>Cancel</button>
                  <button className="mc-workspace__btn mc-workspace__btn--primary" type="button" onClick={confirmRemoveRecipientRule}>Delete Rule</button>
                </div>
              </div>
            </div>
          ) : null}
        </section>
      ) : (
        <>
      <section className="mc-responseRules__summary" aria-label="Notification status">
        <article><span>Queued</span><strong>{notificationSummary.queued.length}</strong><small>Email/WhatsApp waiting for delivery</small></article>
        <article><span>Delivered</span><strong>{notificationSummary.delivered.length}</strong><small>Workspace alerts already available</small></article>
        <article><span>Failed</span><strong>{notificationSummary.failed.length}</strong><small>Needs retry or manual review</small></article>
        <article><span>Read</span><strong>{notificationSummary.read.length}</strong><small>Confirmed seen by recipient</small></article>
        <article><span>Email</span><strong>{notificationSummary.email.length}</strong><small>Email notification records</small></article>
        <article><span>WhatsApp</span><strong>{notificationSummary.whatsapp.length}</strong><small>WhatsApp notification records</small></article>
      </section>

      <section className="mc-responseRules__emailDelivery">
        <div className="mc-responseCenter__panelHead">
          <div><span>Email delivery</span><h3>6B · Real email channel</h3></div>
          <b>{emailConfig.testMode ? "Test mode" : emailConfig.enabled ? "Live endpoint" : "Not configured"}</b>
        </div>
        <div className="mc-responseRules__emailGrid">
          <label className="mc-responseRules__switch">
            <input type="checkbox" checked={Boolean(emailConfig.enabled)} onChange={(event) => updateEmailConfig({ enabled: event.target.checked })} />
            <span>Email enabled</span>
          </label>
          <label className="mc-responseRules__switch">
            <input type="checkbox" checked={Boolean(emailConfig.testMode)} onChange={(event) => updateEmailConfig({ testMode: event.target.checked })} />
            <span>Test mode</span>
          </label>
          <label className="mc-responseRules__field">
            <span>Email endpoint / function URL</span>
            <input value={emailConfig.endpointUrl || ""} onChange={(event) => updateEmailConfig({ endpointUrl: event.target.value })} placeholder="https://.../sendMorpheusEmail" />
          </label>
          <label className="mc-responseRules__field">
            <span>From email</span>
            <input value={emailConfig.fromEmail || ""} onChange={(event) => updateEmailConfig({ fromEmail: event.target.value })} placeholder="alerts@company.com" />
          </label>
          <label className="mc-responseRules__field">
            <span>From name</span>
            <input value={emailConfig.fromName || ""} onChange={(event) => updateEmailConfig({ fromName: event.target.value })} placeholder="Morpheus" />
          </label>
          <label className="mc-responseRules__field">
            <span>Reply-to</span>
            <input value={emailConfig.replyTo || ""} onChange={(event) => updateEmailConfig({ replyTo: event.target.value })} placeholder="ops@company.com" />
          </label>
        </div>
        <div className="mc-responseRules__emailActions">
          <button className="mc-workspace__btn mc-workspace__btn--primary" type="button" onClick={processEmailQueue}>Process Email Queue</button>
          <small>{emailDeliveryStatus || "Test mode marks queued emails as delivered without an external provider. Turn it off when a real endpoint is configured."}</small>
        </div>
      </section>

      <section className="mc-responseRules__emailDelivery mc-responseRules__whatsappDelivery">
        <div className="mc-responseCenter__panelHead">
          <div><span>WhatsApp delivery</span><h3>6E · Real WhatsApp channel</h3></div>
          <b>{whatsappConfig.testMode ? "Test mode" : whatsappConfig.enabled ? "Meta Cloud API" : "Not configured"}</b>
        </div>
        <div className="mc-responseRules__emailGrid">
          <label className="mc-responseRules__switch">
            <input type="checkbox" checked={Boolean(whatsappConfig.enabled)} onChange={(event) => updateWhatsappConfig({ enabled: event.target.checked })} />
            <span>WhatsApp enabled</span>
          </label>
          <label className="mc-responseRules__switch">
            <input type="checkbox" checked={Boolean(whatsappConfig.testMode)} onChange={(event) => updateWhatsappConfig({ testMode: event.target.checked })} />
            <span>Test mode</span>
          </label>
          <label className="mc-responseRules__field">
            <span>Provider</span>
            <select value={whatsappConfig.provider || "meta_cloud_api"} onChange={(event) => updateWhatsappConfig({ provider: event.target.value })}>
              <option value="meta_cloud_api">Meta Cloud API</option>
              <option value="custom_webhook">Custom webhook</option>
            </select>
          </label>
          <label className="mc-responseRules__field">
            <span>Phone Number ID</span>
            <input value={whatsappConfig.phoneNumberId || ""} onChange={(event) => updateWhatsappConfig({ phoneNumberId: event.target.value })} placeholder="Meta WhatsApp phone number ID" />
          </label>
          <label className="mc-responseRules__field">
            <span>Access token</span>
            <input value={whatsappConfig.accessToken || ""} onChange={(event) => updateWhatsappConfig({ accessToken: event.target.value })} placeholder="Paste Meta access token when live" type={whatsappConfig.testMode ? "text" : "password"} />
          </label>
          <label className="mc-responseRules__field">
            <span>Default sender</span>
            <input value={whatsappConfig.defaultSender || ""} onChange={(event) => updateWhatsappConfig({ defaultSender: event.target.value })} placeholder="Morpheus / MatrixOS" />
          </label>
          <label className="mc-responseRules__field">
            <span>Test recipient</span>
            <input value={whatsappConfig.testRecipient || ""} onChange={(event) => updateWhatsappConfig({ testRecipient: event.target.value })} placeholder="+27..." />
          </label>
        </div>
        <div className="mc-responseRules__emailActions">
          <button className="mc-workspace__btn mc-workspace__btn--primary" type="button" onClick={processWhatsappQueue}>Process WhatsApp Queue</button>
          <small>{whatsappDeliveryStatus || "Test mode marks queued WhatsApp notifications as delivered without calling Meta. Turn it off when credentials are configured."}</small>
        </div>
      </section>

      <section className="mc-responseRules__grid">
        {rules.map((rule) => (
          <article className="mc-responseRules__card" key={rule.id}>
            <div className="mc-responseRules__cardHead">
              <label className="mc-responseRules__switch">
                <input type="checkbox" checked={rule.active !== false} onChange={(event) => updateRule(rule.id, { active: event.target.checked })} />
                <span>{rule.active !== false ? "Active" : "Disabled"}</span>
              </label>
              <small>{rule.deliveryMode || "immediate"}</small>
            </div>

            <label className="mc-responseRules__field">
              <span>Rule name</span>
              <input value={rule.name || ""} onChange={(event) => updateRule(rule.id, { name: event.target.value })} />
            </label>

            <div className="mc-responseRules__chips">
              <span>Severity trigger</span>
              {["critical", "high", "medium", "low"].map((severity) => (
                <button key={severity} type="button" className={(rule.severities || []).includes(severity) ? "is-active" : ""} onClick={() => toggleSeverity(rule, severity)}>
                  {responseSeverityLabel(severity)}
                </button>
              ))}
            </div>

            <div className="mc-responseRules__thresholds">
              <label>
                <span>Min exposure</span>
                <input type="number" value={rule.minExposure || 0} onChange={(event) => updateRule(rule.id, { minExposure: Number(event.target.value || 0) })} />
              </label>
              <label>
                <span>Min flagged products</span>
                <input type="number" value={rule.minFlaggedProducts || 0} onChange={(event) => updateRule(rule.id, { minFlaggedProducts: Number(event.target.value || 0) })} />
              </label>
            </div>

            <label className="mc-responseRules__field">
              <span>Recipients</span>
              <input value={(rule.recipients || []).join(", ")} onChange={(event) => updateRule(rule.id, { recipients: event.target.value.split(",").map((item) => item.trim()).filter(Boolean) })} placeholder="Section Owner, Manager" />
            </label>

            <div className="mc-responseRules__chips">
              <span>Channels</span>
              {["workspace", "email", "whatsapp"].map((channel) => (
                <button key={channel} type="button" className={(rule.channels || []).includes(channel) ? "is-active" : ""} onClick={() => toggleChannel(rule, channel)}>
                  {channel}
                </button>
              ))}
            </div>
          </article>
        ))}
      </section>

      <section className="mc-responseRules__queuePreview">
        <div className="mc-responseCenter__panelHead">
          <div><span>Notification store</span><h3>Delivery records and status tracking</h3></div>
          <b>{filteredNotifications.length} shown · {notifications.length} total</b>
        </div>
        <div className="mc-responseRules__queueFilters">
          <label>
            <span>Status</span>
            <select value={notificationStatusFilter} onChange={(event) => setNotificationStatusFilter(event.target.value)}>
              <option value="all">All statuses</option>
              <option value="queued">Queued</option>
              <option value="sending">Sending</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="read">Read</option>
            </select>
          </label>
          <label>
            <span>Channel</span>
            <select value={notificationChannelFilter} onChange={(event) => setNotificationChannelFilter(event.target.value)}>
              <option value="all">All channels</option>
              <option value="workspace">Workspace</option>
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </label>
        </div>
        {filteredNotifications.length ? filteredNotifications.slice(0, 24).map((item) => (
          <div className="mc-responseRules__notificationRow" key={item.id}>
            <div className="mc-responseRules__notificationMain">
              <b>{item.subject}</b>
              <small>{item.recipient} · {item.channel} · {notificationStatusLabel(item.status)} · {formatEventDateTime(item.createdAt)}</small>
              <p>{item.message}</p>
            </div>
            <div className="mc-responseRules__notificationMeta">
              <span className={`mc-responseRules__statusBadge is-${String(item.status || "queued").toLowerCase()}`}>{notificationStatusLabel(item.status)}</span>
              <small>{item.retries ? `${item.retries} retr${Number(item.retries) === 1 ? "y" : "ies"}` : "No retries"}</small>
              <div className="mc-responseRules__notificationActions">
                {item.status !== "delivered" && item.status !== "read" ? <button type="button" onClick={() => setNotificationStatus(item.id, "delivered")}>Mark Delivered</button> : null}
                {item.status !== "failed" ? <button type="button" onClick={() => setNotificationStatus(item.id, "failed")}>Mark Failed</button> : null}
                {item.status === "failed" ? <button type="button" onClick={() => retryQueuedNotification(item.id)}>Retry</button> : null}
                {item.status === "delivered" ? <button type="button" onClick={() => setNotificationStatus(item.id, "read")}>Mark Read</button> : null}
              </div>
            </div>
          </div>
        )) : <p>No notifications match the current filters. Finalize a count with a matching response event to populate this store.</p>}
      </section>
        </>
      )}
      <McPageFooter />
    </div>
  );
}

function MorpheusCountResponseCenter() {
  const currentProfile = useCurrentMcProfile();
  const [tick, setTick] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [periodFilter, setPeriodFilter] = useState("today");
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [lastQueueCheck, setLastQueueCheck] = useState("");

  useEffect(() => {
    let bumpTimer = null;
    const bump = () => {
      if (bumpTimer !== null) return;
      bumpTimer = window.setTimeout(() => {
        bumpTimer = null;
        setTick((value) => value + 1);
      }, 0);
    };
    window.addEventListener(MC_RESPONSE_EVENT, bump);
    window.addEventListener("storage", bump);
    window.addEventListener("mc-cloud-sync", bump);
    return () => {
      window.removeEventListener(MC_RESPONSE_EVENT, bump);
      window.removeEventListener("storage", bump);
      window.removeEventListener("mc-cloud-sync", bump);
      if (bumpTimer !== null) window.clearTimeout(bumpTimer);
    };
  }, []);

  const events = useMemo(() => loadResponseEvents(), [tick]);
  const periodEvents = useMemo(() => events.filter((event) => matchesResponsePeriod(event?.createdAt, periodFilter)), [events, periodFilter]);
  const filtered = useMemo(() => periodEvents.filter((event) => {
    const status = String(event.status || "open").toLowerCase();
    if (statusFilter === "active") return !["resolved", "dismissed"].includes(status);
    return statusFilter === "all" || status === statusFilter;
  }).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)), [periodEvents, statusFilter]);
  const selected = periodEvents.find((event) => event.id === selectedId) || filtered[0] || null;
  const summary = buildResponseEventSummary(periodEvents);
  const actorName = currentProfile?.name || currentProfile?.fullName || "Operator";

  const setStatus = (status) => {
    if (!selected) return;
    updateResponseEventStatus(selected.id, status, actorName);
    setTick((value) => value + 1);
  };

  return (
    <div className="mc-workspace mc-tabPage">

      <McPageHeader activeKey="responses" />
      <section className="mc-responseCenter__metrics">
        <article><span>Open</span><strong>{summary.open?.length || 0}</strong><small>Waiting for action</small></article>
        <article><span>Investigating</span><strong>{summary.investigating?.length || 0}</strong><small>Being handled</small></article>
        <article><span>Awaiting verification</span><strong>{periodEvents.filter((event) => String(event.status || "").toLowerCase() === "awaiting_verification").length}</strong><small>Ready to confirm</small></article>
        <article><span>Resolved</span><strong>{summary.resolved?.length || 0}</strong><small>Closed responses</small></article>
      </section>
      <section className="mc-responseCenter__filters">
        <label>Period<select value={periodFilter} onChange={(e) => { setPeriodFilter(e.target.value); setSelectedId(""); }}><option value="today">Today</option><option value="7d">Last 7 days</option><option value="30d">Last 30 days</option><option value="all">All time</option></select></label>
        <label>Status<select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="active">Active only</option><option value="all">All</option><option value="open">Open</option><option value="investigating">Investigating</option><option value="awaiting_verification">Awaiting verification</option><option value="resolved">Resolved</option></select></label>
      </section>
      <section className="mc-responseCenter__layout">
        <div className="mc-responseCenter__queue">
          <div className="mc-responseCenter__sectionHeader"><div><span>Response queue</span><h2>{filtered.length} events</h2></div></div>
          {filtered.map((event) => <button key={event.id} type="button" className={`mc-responseCenter__queueItem ${selected?.id === event.id ? "is-selected" : ""}`} onClick={() => setSelectedId(event.id)}>
            <div><strong>{event.siteName}</strong><span>{event.sectionName}</span></div>
            <b className={`is-${String(event.severity || "low").toLowerCase()}`}>{formatCurrency(event.exposure)}</b>
            <small>{event.flaggedProducts || 0} flagged · {String(event.status || "open").replace(/_/g, " ")}</small>
          </button>)}
          {!filtered.length ? <div className="mc-notificationCenter__empty">No responses match this status.</div> : null}
        </div>
        <aside className="mc-responseCenter__detail">
          {selected ? <>
            <div className="mc-responseCenter__sectionHeader"><div><span>Response detail</span><h2>{selected.siteName} · {selected.sectionName}</h2></div></div>
            <div className="mc-responseCenter__hero"><span>{responseSeverityLabel(selected.severity)}</span><strong>{formatCurrency(selected.exposure)}</strong><p>{selected.flaggedProducts || 0} flagged product{Number(selected.flaggedProducts || 0) === 1 ? "" : "s"}</p></div>
            <div className="mc-responseCenter__metaGrid">
              <article><span>Status</span><strong>{String(selected.status || "open").replace(/_/g, " ")}</strong></article>
              <article><span>Assigned to</span><strong>{selected.assignedTo || "Unassigned"}</strong></article>
              <article><span>Cycle</span><strong>{selected.cycleName || selected.cycle || "Unassigned cycle"}</strong></article>
              <article><span>Counted by</span><strong>{selected.countedBy || selected.startedBy || "User"}</strong></article>
            </div>
            <div className="mc-responseCenter__actions">
              <button onClick={() => setStatus("open")}>Open</button>
              <button onClick={() => setStatus("investigating")}>Investigating</button>
              <button onClick={() => setStatus("awaiting_verification")}>Awaiting Verification</button>
              <button onClick={() => setStatus("resolved")}>Resolve</button>
            </div>
            <Link className="mc-workspace__btn mc-workspace__btn--ghost" to={`/morpheus-count/session/${selected.sessionId || selected.sourceSessionId || ""}`}>View Count</Link>
          </> : <div className="mc-notificationCenter__empty">Select a response.</div>}
        </aside>
      </section>
      <McPageFooter />
    </div>
  );
}

export default function MorpheusCountWorkspace() {
  const [period, setPeriod] = useState("7d");
  const [historyStatus, setHistoryStatus] = useState("all");
  const [dashboardView, setDashboardView] = useState("overview");
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const location = useLocation();
  const activeHash = location.hash || (typeof window === "undefined" ? "" : window.location.hash);
  const currentProfile = useCurrentMcProfile();
  const adminStoreView = useAdminStoreView();
  // Use the same authoritative tenant identity as Sessions and the live-session
  // Firestore subscriber. Owner/admin profile objects can hydrate without an orgId
  // for a short period, which previously caused Dashboard to reject valid live rows.
  const dashboardOrgId = String(getMcActiveOrgId() || currentProfile?.organizationId || currentProfile?.orgId || "").trim();
  const [dashboardLiveRecords, setDashboardLiveRecords] = useState(null);
  const [dashboardLedgerValues, setDashboardLedgerValues] = useState({});
  const [liveSessionsHydrated, setLiveSessionsHydrated] = useState(false);
  const [liveSessionTick, setLiveSessionTick] = useState(0);
  const [snapshotReady, setSnapshotReady] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshTimerRef = useRef([]);

  const clearRefreshTimers = useCallback(() => {
    refreshTimerRef.current.forEach((timer) => window.clearTimeout(timer));
    refreshTimerRef.current = [];
  }, []);

  const runRefreshPipeline = useCallback(({ full = false } = {}) => {
    clearRefreshTimers();
    if (full) setSnapshotReady(false);
    setIsRefreshing(true);
    setLiveSessionTick((value) => value + 1);
    refreshTimerRef.current.push(window.setTimeout(() => {
      setSnapshotReady(true);
      setIsRefreshing(false);
    }, 180));
  }, [clearRefreshTimers]);

  useEffect(() => {
    runRefreshPipeline({ full: true });
    return clearRefreshTimers;
  }, [adminStoreView, dashboardOrgId, runRefreshPipeline, clearRefreshTimers]);

  useEffect(() => {
    // Sessions and Dashboard now consume the exact same canonical live-session
    // subscription. Recreate it whenever the active organisation changes so a
    // listener opened for the previous tenant can never remain authoritative.
    setDashboardLiveRecords(null);
    setLiveSessionsHydrated(false);
    let hydrationFallbackTimer = null;

    // External MC events can be dispatched synchronously while another route is
    // rendering (Archives is the most common source during rapid tab changes).
    // Never let those event callbacks call React setters in the same stack.
    // Coalesce them onto a single macrotask so Count Workspace refreshes once
    // after the current render/route transition has fully completed.
    let externalRefreshTimer = null;
    let pendingLiveRecords = null;
    let pendingLiveRecordsOrgId = "";

    const flushExternalRefresh = () => {
      externalRefreshTimer = null;
      if (Array.isArray(pendingLiveRecords)) {
        const records = pendingLiveRecords;
        const sourceOrgId = pendingLiveRecordsOrgId;
        pendingLiveRecords = null;
        pendingLiveRecordsOrgId = "";
        if (!sourceOrgId || !dashboardOrgId || sourceOrgId === dashboardOrgId) {
          setDashboardLiveRecords(records);
          setLiveSessionsHydrated(true);
        }
      }
      setLiveSessionTick((value) => value + 1);
    };

    const scheduleExternalRefresh = (records = null, sourceOrgId = "") => {
      if (Array.isArray(records)) {
        pendingLiveRecords = records;
        pendingLiveRecordsOrgId = String(sourceOrgId || "").trim();
      }
      if (externalRefreshTimer !== null) return;
      externalRefreshTimer = window.setTimeout(flushExternalRefresh, 0);
    };

    const applyLiveRecords = (records, meta = {}) => {
      const sourceOrgId = String(meta?.orgId || "").trim();
      if (sourceOrgId && dashboardOrgId && sourceOrgId !== dashboardOrgId) return;
      if (!Array.isArray(records)) return;
      scheduleExternalRefresh(records, sourceOrgId);
    };

    const onSessionEvent = (event) => {
      const detail = event?.detail || {};
      const sourceOrgId = String(detail?.orgId || "").trim();
      if (sourceOrgId && dashboardOrgId && sourceOrgId !== dashboardOrgId) return;
      if (Array.isArray(detail?.records)) {
        scheduleExternalRefresh(detail.records, sourceOrgId);
      } else {
        scheduleExternalRefresh();
      }
    };

    const bump = () => {
      // Non-session operational events rebuild the memoized snapshot silently.
      // The visible Refresh animation remains manual-only.
      scheduleExternalRefresh();
    };

    const unsubscribeLiveSessions = dashboardOrgId
      ? subscribeSessionListLive((records, meta) => applyLiveRecords(records, { ...meta, orgId: dashboardOrgId }))
      : null;
    window.addEventListener(MC_SESSION_EVENT, onSessionEvent);
    window.addEventListener("storage", bump);
    window.addEventListener("mc-cloud-sync", bump);
    window.addEventListener("mc-cloud-sync-ready", bump);
    hydrationFallbackTimer = window.setTimeout(() => {
      // Keep the real loader visible while Firestore hydrates, but never strand
      // the dashboard forever if the organisation has no session document yet.
      setLiveSessionsHydrated(true);
    }, 4000);
    return () => {
      if (hydrationFallbackTimer) window.clearTimeout(hydrationFallbackTimer);
      if (externalRefreshTimer !== null) window.clearTimeout(externalRefreshTimer);
      pendingLiveRecords = null;
      pendingLiveRecordsOrgId = "";
      unsubscribeLiveSessions?.();
      window.removeEventListener(MC_SESSION_EVENT, onSessionEvent);
      window.removeEventListener("storage", bump);
      window.removeEventListener("mc-cloud-sync", bump);
      window.removeEventListener("mc-cloud-sync-ready", bump);
    };
  }, [dashboardOrgId]);

  useEffect(() => {
    // P4.2 — Dashboard is a read-only projection of the same canonical Count
    // Ledgers as Live Count and Sessions. mc-session-list is discovery only.
    const records = Array.isArray(dashboardLiveRecords) ? dashboardLiveRecords : [];
    const ids = Array.from(new Set(records.map((item) => String(item?.sessionId || '').trim()).filter(Boolean)));
    if (!dashboardOrgId || !ids.length) {
      setDashboardLedgerValues({});
      return undefined;
    }

    let active = true;
    setDashboardLedgerValues((current) => {
      const next = {};
      ids.forEach((id) => { if (current[id]) next[id] = current[id]; });
      return next;
    });

    const unsubscribers = ids.map((sessionId) => mcSubscribeCloudItem(`mc-count-ledger:${sessionId}`, (value) => {
      if (!active) return;
      setDashboardLedgerValues((current) => {
        if (current[sessionId] === value) return current;
        return { ...current, [sessionId]: value || null };
      });
      setLiveSessionTick((valueTick) => valueTick + 1);
    }));

    return () => {
      active = false;
      unsubscribers.forEach((unsubscribe) => unsubscribe?.());
    };
  }, [dashboardOrgId, dashboardLiveRecords]);

  const dashboard = useMemo(() => {
    // P4.2: mc-session-list discovers live session IDs only. Operational truth
    // (progress, exposure, flags, completion and verification) comes exclusively
    // from mc-count-ledger:<sessionId>. Prepared seeds remain non-operational drafts.
    const archivedRaw = loadArchivedSessionRecords();
    const archivedIds = new Set(archivedRaw.map((item) => String(item?.sessionId || "").trim()).filter(Boolean));
    const canonicalLiveRecords = Array.isArray(dashboardLiveRecords)
      ? dashboardLiveRecords
      : loadSessionRecords();
    const activeRecords = canonicalLiveRecords
      .filter((item) => {
        const tenantOrgId = dashboardRecordOrgId(item);
        return Boolean(dashboardOrgId && tenantOrgId === dashboardOrgId);
      })
      .filter((item) => dashboardLiveRecordMatchesCurrentInventory(item))
      .filter((item) => {
        const id = String(item?.sessionId || "").trim();
        return id && !archivedIds.has(id);
      })
      .filter((item) => item?.archived !== true && !item?.archivedAt && !item?.archiveReason && !item?.archiveType)
      .map((item) => {
        const sessionId = String(item?.sessionId || '').trim();
        const ledgerValue = dashboardLedgerValues[sessionId] || mcGetItem(`mc-count-ledger:${sessionId}`);
        const projected = projectDashboardSessionFromLedger(item, ledgerValue);
        // Discovery can arrive milliseconds before the ledger listener. Preserve
        // routing metadata but never trust session-list operational summaries.
        return projected || {
          ...item,
          status: 'Active',
          completedLines: 0,
          flaggedCount: 0,
          costExposure: 0,
          dashboardLedgerReady: false,
        };
      })
      .map((item) => ({ ...item, dashboardSource: "active" }));
    const archivedRecords = archivedRaw.map(resolveDashboardArchivedRecord);

    const effectiveStoreView = adminStoreView || getAdminStoreView();
    // Put archives first so if an old active/session-list ghost has the same ID,
    // the finalized archive snapshot wins every time.
    const combined = uniqueSessions(filterDashboardRecords([...archivedRecords, ...activeRecords], currentProfile, effectiveStoreView, dashboardOrgId))
      .sort((a, b) => sessionDate(b) - sessionDate(a));

    const scope = effectiveStoreView && effectiveStoreView !== "ALL"
      ? formatStoreLabel(effectiveStoreView) : isAdminProfile(currentProfile) ? "All Sites" : "Your sites";
    return buildSessionDashboard(combined.map((record) => ({ ...record, dashboardSiteLabel: sessionStoreLabel(record) })), { scope });
  }, [adminStoreView, currentProfile, liveSessionTick, dashboardLiveRecords, dashboardLedgerValues, dashboardOrgId]);

  const history = useMemo(() => buildSessionHistory(dashboard, { period, status: historyStatus }), [dashboard, period, historyStatus]);

  const canExportDashboard = isAdminProfile(currentProfile);

  const exportDashboardPdf = async () => {
    if (isExporting || !snapshotReady || !liveSessionsHydrated || !canExportDashboard) return;
    setIsExporting(true);
    setExportError("");
    try {
      const { exportSessionDashboardPdf } = await import("../utils/mcSessionDashboardPdf");
      exportSessionDashboardPdf(dashboard, {
        view: dashboardView, history, light: isMorpheusLightTheme(),
        exporterName: currentProfile?.displayName || currentProfile?.name || currentProfile?.profileName || currentProfile?.email || "",
      });
    } catch (error) {
      console.error("Sessions dashboard PDF export failed", error);
      setExportError("Could not export the sessions report. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  if (activeHash === "#responses") {
    return <MorpheusCountResponseCenter />;
  }

  if (activeHash === "#response-rules") {
    return <MorpheusCountResponseRules />;
  }

  if (activeHash === "#notifications") {
    return <MorpheusCountNotificationCenter />;
  }

  if (activeHash === "#billing") {
    return <MorpheusCountBillingContent />;
  }

  if (activeHash === "#tickets") {
    if (!isAdminProfile(currentProfile)) {
      window.location.hash = "";
      return null;
    }
    return <MorpheusCountTicketsContent />;
  }

  return (
    <div className="mc-workspace mc-sessionDashboard mc-tabPage">

      <McPageHeader
        activeKey="workspace" actionsInHeader
        actions={(
          <div className="mc-sessionDashHeaderActions">
            <Link className="mc-workspace__btn mc-workspace__btn--primary" to="/morpheus-count/sessions">Open Sessions</Link>
            {canExportDashboard ? <button className="mc-workspace__btn mc-workspace__btn--ghost" type="button" onClick={exportDashboardPdf}
              disabled={isExporting || isRefreshing || !snapshotReady || !liveSessionsHydrated}>{isExporting ? "Exporting…" : "Export PDF"}</button> : null}
          </div>
        )}
      />
      {exportError ? <p className="mc-sessionDashExportError" role="alert">{exportError}</p> : null}
      <McSessionDashboard dashboard={dashboard} history={history} view={dashboardView} setView={setDashboardView}
        setPeriod={setPeriod} setStatus={setHistoryStatus} ready={snapshotReady && liveSessionsHydrated}
        canVerify={isAdminProfile(currentProfile)} canFinalize={canFinalizeCount(currentProfile)} />
      <McPageFooter />
    </div>
  );
}
